import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allArticles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = allArticles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-16 text-center">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要查看的文章不存在或已被删除</p>
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回分类浏览
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '蜜桃在线',
    },
    image: article.image,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mitaozaixian.com/' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.mitaozaixian.com/category' },
      { '@type': 'ListItem', position: 3, name: article.categoryName, item: `https://www.mitaozaixian.com/category?cat=${article.category}` },
      { '@type': 'ListItem', position: 4, name: article.title },
    ],
  };

  const fullContent = `${article.excerpt}

蜜桃在线一直以来致力于为用户提供最优质的在线娱乐体验。我们深知，在当今快节奏的生活中，人们需要一个能够放松身心、获取乐趣的平台。正是在这样的理念驱动下，蜜桃在线不断优化产品体验，丰富内容生态。

${article.categoryName}是蜜桃在线平台的核心板块之一。我们拥有一支专业的内容策划团队，每天精心筛选和推荐最优质的内容，确保每一位用户都能在这里找到自己喜爱的内容。无论是追求刺激的冒险者，还是偏爱宁静的文艺青年，蜜桃在线都能为您提供恰到好处的内容选择。

在技术层面，蜜桃在线采用了行业领先的智能推荐算法。通过深度学习用户的浏览习惯和兴趣偏好，我们的系统能够在海量内容中精准匹配最适合您的资源。这种个性化推荐不仅节省了用户筛选内容的时间，更大大提升了内容消费的满意度。

安全性始终是蜜桃在线的核心关注点。我们建立了严格的七层内容审核机制，由AI初审和人工复审相结合，确保平台上的所有内容都符合法律法规和社区准则。同时，我们采用银行级别的数据加密技术，全方位保护用户的隐私信息。

蜜桃在线还特别注重社区氛围的建设。我们鼓励用户在平台上积极互动、分享心得，形成了温暖友好的社区文化。定期的线上活动和丰富的互动玩法，让每一位用户都能感受到归属感和参与感。

展望未来，蜜桃在线将继续秉持"用户至上、内容为王"的理念，持续加大技术研发和内容投入，不断拓展新的内容领域和互动形式。我们相信，通过不懈的努力和创新，蜜桃在线将成为越来越多人首选的在线娱乐平台。

如果您对蜜桃在线有任何建议或意见，欢迎随时通过官方客服渠道与我们联系。我们珍视每一位用户的反馈，因为您的满意是我们前进的最大动力。`;

  return (
    <Layout>
      <SEOHead
        title={`${article.title} - 蜜桃在线`}
        description={article.excerpt}
        keywords={`蜜桃在线,${article.categoryName},${article.title},蜜桃在线官网,在线娱乐`}
        canonicalUrl={`https://www.mitaozaixian.com/article/${article.id}`}
        ogType="article"
      />

      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览', path: '/category' },
              { label: article.categoryName, path: `/category?cat=${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读时间约{article.readTime}
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 蜜桃在线`}
                className="w-full h-auto object-cover max-h-[480px]"
              />
            </div>

            <div className="prose-custom text-foreground-800 leading-relaxed">
              {fullContent.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-sm md:text-base leading-relaxed">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  蜜桃在线
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  在线娱乐
                </span>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-xl font-heading text-foreground-900 mb-6">相关文章推荐</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={`${ra.title} - 蜜桃在线`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                        <span className="text-xs text-foreground-400 mt-1.5 block">{ra.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </div>
    </Layout>
  );
}